export const validatePersonaName = (name: string): { valid: boolean; message?: string } => {
  if (!name || name.trim().length === 0) {
    return { valid: false, message: 'Name is required' };
  }

  if (name.length < 3) {
    return { valid: false, message: 'Name must be at least 3 characters' };
  }

  // Guards for legacy/backend-origin names and programmatic callers.
  // Not reached during normal input flow — normalizePersonaName is authoritative.
  // Do NOT remove these checks even if they look dead — the transform is a UI
  // affordance, not a security boundary.
  if (name.length > 80) {
    return { valid: false, message: 'Name must be at most 80 characters' };
  }

  if (!/^[a-zA-Z0-9_]+$/.test(name)) {
    return { valid: false, message: 'Name must contain only letters, numbers, and underscores' };
  }

  // Soft warning — validator returns valid:true so form submission is allowed.
  // Consumers render this in subdued styling, not error styling.
  if (!/^[A-Z]/.test(name)) {
    return { valid: true, message: 'Recommended: Start with an uppercase letter' };
  }

  return { valid: true };
};

/**
 * LIVE keystroke transform. Does NOT strip leading/trailing underscores —
 * that would eat single-underscore / single-space / single-hyphen input
 * before the user can type the next character (which is how most typed
 * names build up, e.g. '_Foo', 'Foo_Bar', 'Creative Writing Coach').
 *
 * NARROW-CONTRACT: For persona-name (slug-like) fields ONLY.
 *
 * Pure, synchronous, idempotent.
 *
 * Steps:
 *   1. Replace every run of characters outside [A-Za-z0-9_] with a single '_'.
 *   2. Collapse consecutive underscores to one.
 *   3. Clamp to max 80 characters by right-truncation.
 *
 * Edge stripping is split into `finalizePersonaName` (below) which runs on
 * blur / composition-end / submit, NOT on every keystroke.
 */
export function normalizePersonaName(raw: string): string {
  return raw
    .replace(/[^A-Za-z0-9_]+/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 80);
}

/**
 * FINAL normalization — strips leading and trailing underscores.
 *
 * Call on blur / composition-end / submit, NOT on every keystroke.
 * The live-strip variant was too aggressive: it made it impossible to
 * type '_Foo' or 'Foo_Bar' left-to-right because the underscore vanished
 * before the next character arrived.
 *
 * Pure, synchronous, idempotent.
 */
export function finalizePersonaName(raw: string): string {
  return normalizePersonaName(raw).replace(/^_+|_+$/g, '');
}
